import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface SiteMovePreviewRequest {
  siteId: string;
  targetParentId: string;
  reason: string;
}

export interface SiteMoveRoleSummary {
  roleId: string;
  name?: string;
}

export interface SiteMoveParentRolePlan {
  userId: string;
  userDisplayName?: string;
  userEmail?: string;
  userAccessPolicy: 'DeactivateSiteMembershipAndResetToViewer';
  siteMembershipEnabled: boolean;
  siteRoleIds: string[];
  existingTargetRoleIds: string[];
  sourceRoleIds: string[];
  targetRoleIds: string[];
  sourceRoles?: SiteMoveRoleSummary[];
  targetRoles?: SiteMoveRoleSummary[];
  siteRoles?: SiteMoveRoleSummary[];
  existingTargetRoles?: SiteMoveRoleSummary[];
}

export interface SiteMoveAppliance {
  serialNumber: string;
  friendlyName?: string | null;
  provisioned: boolean;
  enabled: boolean;
}

export interface SiteMovePreview {
  siteId: string;
  sourceParentId: string;
  targetParentId: string;
  sourceParentName: string;
  targetParentName: string;
  sourceParentVersion: number;
  appliances?: SiteMoveAppliance[] | null;
  approvedParentRolePlan: SiteMoveParentRolePlan[];
  blockers: { code: string; detail: string }[];
  canExecute: boolean;
}

export interface SiteMoveExecuteRequest extends SiteMovePreviewRequest {
  operationId: string;
  expectedSourceParentId: string;
  expectedSourceVersion: number;
  approvedParentRolePlan: SiteMoveParentRolePlan[];
}

export interface SiteMoveSystem {
  assetId: string;
  name: string;
  machineType: string;
  machineNo: string;
  assetEnabled: boolean;
  associationEnabled: boolean;
}

export interface SiteMoveResult {
  operationId: string;
  siteId: string;
  sourceParentId: string;
  targetParentId: string;
  state: 'Succeeded' | 'Failed';
  errorCode?: string;
  errorDetail?: string;
}

export interface SiteMoveHistoryRolePlan {
  userId: string;
  sourceRoleIds: string[];
  targetRoleIds: string[];
  userAccessPolicy?: string | null;
  siteMembershipEnabled?: boolean | null;
  siteRoleIds?: string[] | null;
  existingTargetRoleIds?: string[] | null;
}

export interface SiteMoveHistoryItem {
  operationId: string;
  siteId: string;
  siteName?: string;
  sourceParentId: string;
  sourceParentName?: string;
  targetParentId: string;
  targetParentName?: string;
  actorId: string;
  actorName?: string;
  reason: string;
  state: string;
  errorCode?: string;
  errorDetail?: string;
  recordedAtUtc: string;
  approvedParentRolePlan: SiteMoveHistoryRolePlan[];
}

export interface SiteMoveHistoryPage {
  items: SiteMoveHistoryItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export type SiteMoveCapability = 'read' | 'write';
export interface SiteMoveAccess { allowed: boolean; }

@Injectable({ providedIn: 'root' })
export class SiteMoveService {
  private readonly url = `${environment.apiUrl.authorizationService}site-moves`;

  constructor(private readonly http: HttpClient) {}

  access(capability: SiteMoveCapability): Observable<SiteMoveAccess> {
    return this.http.get<SiteMoveAccess>(`${this.url}/access`, { params: { capability } });
  }

  history(page = 1, pageSize = 25): Observable<SiteMoveHistoryPage> {
    return this.http.get<SiteMoveHistoryPage>(`${this.url}/history`, { params: { page, pageSize } });
  }

  preview(request: SiteMovePreviewRequest): Observable<SiteMovePreview> {
    return this.http.post<SiteMovePreview>(`${this.url}/preview`, request);
  }

  getSystems(siteId: string): Observable<SiteMoveSystem[]> {
    return this.http.get<SiteMoveSystem[]>(`${this.url}/sites/${siteId}/systems`);
  }

  execute(request: SiteMoveExecuteRequest): Observable<SiteMoveResult> {
    return this.http.post<SiteMoveResult>(this.url, request);
  }

  getStatus(operationId: string): Observable<SiteMoveResult> {
    return this.http.get<SiteMoveResult>(`${this.url}/${operationId}`);
  }
}
